import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup
} from "firebase/auth";
import { auth, googleProvider } from "../services/firebase";
import { logUiEvent } from "../services/analytics";

function LoginPage({ user }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const redirectToSignupClosed = (err) => {
    const message =
      err?.customData?.message || err?.message || "";

    if (message.toLowerCase().includes("signups_disabled")) {
      logUiEvent("auth_signup_blocked");
      navigate("/signup-closed", { replace: true });
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (user) {
      const hasBillingPlan = user?.billingInfo?.plan != null;
      navigate(hasBillingPlan ? "/dashboard" : "/plan", { replace: true });
    }
  }, [user, navigate]);

  const handleEmailAuth = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (mode === "signup") {
        await createUserWithEmailAndPassword(auth, email, password);
        logUiEvent("auth_signup_email");
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        logUiEvent("auth_login_email");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (redirectToSignupClosed(err)) {
        return;
      }
      setError(err.message);
      logUiEvent("auth_error", { provider: mode, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    setError("");

    try {
      await signInWithPopup(auth, googleProvider);
      logUiEvent("auth_login_google");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (redirectToSignupClosed(err)) {
        return;
      }
      setError(err.message);
      logUiEvent("auth_error", { provider: "google", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <img src="/logo192.png" alt="gofannon" />
          <div>
            <div className="brand__title">RamenAtA</div>
            <div className="brand__subtitle">Home of the Flash App</div>
          </div>
        </div>

        <div className="auth-toggle">
          <button
            type="button"
            className={`button secondary ${mode === "login" ? "active" : ""}`}
            onClick={() => setMode("login")}
            disabled={loading}
          >
            Sign in
          </button>
          <button
            type="button"
            className={`button secondary ${mode === "signup" ? "active" : ""}`}
            onClick={() => setMode("signup")}
            disabled={loading}
          >
            Create account
          </button>
        </div>

        <form className="auth-form" onSubmit={handleEmailAuth}>
          <label className="auth-label">
            Email
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={loading}
            />
          </label>

          <label className="auth-label">
            Password
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button className="button" type="submit" disabled={loading}>
            {loading ? "Working..." : mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button
          className="button secondary full-width"
          type="button"
          onClick={handleGoogleAuth}
          disabled={loading}
        >
          Continue with Google
        </button>
      </div>
    </div>
  );
}

export default LoginPage;
